import { FunctionComponent, SetStateAction, useEffect, useMemo, useState } from 'react';
import styles from './WalkingComponent.module.scss';
import { DataVaraible, SessionAddressOverride, Walking, versionPetAndServices } from 'model/NewMultipleBooking';
import { useApiClientWrapper, useMarketplace, usePermissions, useTimeZone } from 'hooks';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { ServicesStatus } from 'reducers/services/ServicesState';
import { NewBookingsStatus } from '@spike/new-bookings-action';
import { StaffStatus } from 'reducers/staff/StaffState';
import Button from 'components/UI/V3/Button/Button';
import StaffSelector from '../StaffSelector/StaffSelector';
import WalkingServiceSelector from './UI/WalkingServiceSelector/WalkingServiceSelector';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { ValueTypeSelector } from 'components/UI/V3/Forms/ValueTypeSelector';
import DurationSelect from '../ServiceSelector/DurationSelect';
import PriceField from 'components/UI/V3/Forms/PriceField';
import { ClientsStatus, getClientThunk } from '@spike/clients-action';
import { OrdersStatus } from '@spike/orders-action';
import useNonInitialEffect from '@versiondos/hooks';
import Client from '@spike/client-model';
import Spinner from 'components/UI/V3/Spinner';
import { fetchStaffThunk } from 'actions/staff/StaffActions';
import Staff from 'model/Staff';
import { SERVICES_BUSINESS_AREAS, WalkingService } from 'model/Service';
import { SelectedPlace } from 'hooks/usePlacesAutocomplete';
import { RecentlyBooked } from '../UI/RecentlyBooked';
import { AppointmentHistoryDrawer } from '../UI/AppointmentHistoryDrawer';
import OrderDetail from 'components/OrderDetail';
import { AppointmentListItem } from '@spike/appointments-list-model';
import clsx from 'clsx';
import lockStyles from '../UI/priceFieldInteractionLock.module.scss';
import type { Enrollment, MembershipItem } from '@spike/membership-model';
import type { EnrollmentAnchor } from '../utils/enrollmentAnchor';
import { PetCustomPrice } from '@spike/pet-model';
import { fetchPetCustomPricesThunk } from '@spike/pets-action';
import { resolveStandardPrice } from '../utils/bookingCustomPrices';
import SaveCustomPriceCheckbox from '../UI/SaveCustomPriceCheckbox';
import SavedCustomPriceChoice from '../UI/SavedCustomPriceChoice';
import { useSavedCustomPriceChoice } from '../UI/useSavedCustomPriceChoice';
import MultipleDateSelector from '../DateSelector/MultipleDateSelector';
import PeriodSelector from '../DateSelector/PeriodSelector';
import BookingAlertList from '../UI/BookingAlertList';
import SessionFullAlert from '../UI/SessionFullAlert';
import SessionSelector from '../UI/SessionSelector/SessionSelector';
import ClientAddressAutocomplete from '../UI/ClientAddressAutocomplete/ClientAddressAutocomplete';
import useBookingAlerts from '../UI/useBookingAlerts';
import { mergeRoutedAlerts, splitSessionAlerts } from '../UI/bookingAlerts';
import useSessionAlerts from '../UI/useSessionAlerts';
import useSessionPicker from '../UI/useSessionPicker';
import useClientAddressPrefill from '../UI/useClientAddressPrefill';
import { WalkingSession } from '@spike/new-booking-model';
import { noop } from 'lodash';
import moment, { Moment } from 'moment-timezone';

interface WalkingProps {
    clientId: number;
    petId: number;
    walking: Walking;
    appointmentId?: number;
    servicesUsed?: Array<number>;
    inHomeService?: boolean;
    enrollment?: Enrollment;
    membershipItem?: MembershipItem;
    enrollmentAnchor?: EnrollmentAnchor;
    versionPetAndServices?: versionPetAndServices;
    // Session bookings already added to this booking, minus the one being edited.
    draftSessionBookings?: Array<DataVaraible>;
    onNext: (walking: Walking) => void;
}

// Alerts are deliberately not an input here: Marketplace staff may intentionally book
// outside business hours, staff hours, the service zone or into a full session.
const isSaveDisabled = (form: {
    hasService: boolean;
    hasStaff: boolean;
    needsAddress: boolean;
    hasAddress: boolean;
    durationMinutes: number;
    priceInvalid: boolean;
    missingDates: boolean;
}): boolean =>
    !form.hasService ||
    !form.hasStaff ||
    (form.needsAddress && !form.hasAddress) ||
    form.durationMinutes <= 0 ||
    form.priceInvalid ||
    form.missingDates;

export const WalkingComponent: FunctionComponent<WalkingProps> = ({
    clientId,
    petId,
    walking,
    appointmentId,
    servicesUsed,
    inHomeService,
    enrollment,
    membershipItem,
    versionPetAndServices: versionMode,
    draftSessionBookings = [],
    onNext
}) => {
    const permissions = usePermissions();
    const canEditPrices = permissions.checkout.editPrices;
    const canApplyDiscount = permissions.checkout.applyDiscounts;
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();
    const timeZone = useTimeZone();
    const tz = marketplace.schedule.timeZone;
    const newBookingStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);
    const ordersStatus = useSelector<RootState, OrdersStatus>(state => state.orders.status);
    const servicesStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);
    const isEdit = versionMode === versionPetAndServices.edit;

    const normalizeWalkingDates = (walkingData: Walking): Walking => {
        if (!walkingData) return walkingData;
        return {
            ...walkingData,
            from:
                walkingData.from && walkingData.from.isValid()
                    ? walkingData.from.clone().tz(timeZone)
                    : walkingData.from,
            to: walkingData.to && walkingData.to.isValid() ? walkingData.to.clone().tz(timeZone) : walkingData.to,
            selectedDates: walkingData.selectedDates?.map(d => d.clone().tz(tz).startOf('day'))
        };
    };

    const [walkingState, setWalking] = useState<Walking>(() => normalizeWalkingDates(walking));
    const [selectedDates, setSelectedDates] = useState<Array<Moment>>(
        walking?.selectedDates?.length
            ? walking.selectedDates.map(d => d.clone().tz(tz).startOf('day'))
            : walking?.from
            ? [walking.from.clone().tz(tz).startOf('day')]
            : []
    );
    const clientsStatus = useSelector<RootState, ClientsStatus>(state => state.clients.status);
    const client = useSelector<RootState, Client | undefined>(state => state.clients.client);
    const staffs = useSelector<RootState, Array<Staff>>(state => state.staff.staff);
    const staffStatus = useSelector<RootState, StaffStatus>(state => state.staff.status);
    const customPrices = useSelector<RootState, Array<PetCustomPrice>>(state => state.pets.customPrices);
    const [inHomeServiceState, setInHomeService] = useState<boolean | undefined>(inHomeService);
    const [loading, setLoading] = useState(false);
    const [showAppointmentHistoryDrawer, setShowAppointmentHistoryDrawer] = useState(false);
    const [recentlyBookedRefreshKey, setRecentlyBookedRefreshKey] = useState(0);
    const [showOrderDetail, setShowOrderDetail] = useState<{ orderId: number; bookingId: number } | null>(null);

    const address = walkingState?.address ?? '';
    // Free text is never geocoded here, so the override only exists after a Places pick.
    const addressOverride: SessionAddressOverride | undefined = walkingState?.service?.inHomeService
        ? walkingState?.addressOverride
        : undefined;
    const hasDiscount = (walkingState?.discount?.value ?? 0) > 0;
    const savedCustomPrice = useSavedCustomPriceChoice({
        petId,
        serviceId: walkingState?.service?.serviceId,
        catalogPrice: Number(walkingState?.service?.price) || 0,
        currentPrice: Number(walkingState?.price) || 0,
        useSavedCustomPrice: walkingState?.useSavedCustomPrice
    });

    useEffect(() => {
        if (Number(client?.id) !== Number(clientId)) {
            dispatch(getClientThunk(apiClientWrapper, clientId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- apiClientWrapper inestable por diseño
    }, [clientId, client?.id, dispatch]);

    useEffect(() => {
        if (petId) {
            dispatch(fetchPetCustomPricesThunk(apiClientWrapper, petId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [petId]);

    useEffect(() => {
        setInHomeService(inHomeService);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inHomeService]);

    useEffect(() => {
        if (walkingState?.service?.serviceId) {
            dispatch(fetchStaffThunk(apiClientWrapper.marketplaceId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [walkingState?.service?.serviceId, apiClientWrapper.marketplaceId]);

    useEffect(() => {
        setWalking(normalizeWalkingDates(walking));
        if (walking?.selectedDates?.length) {
            setSelectedDates(walking.selectedDates.map(d => d.clone().tz(tz).startOf('day')));
        } else if (walking?.from?.isValid()) {
            setSelectedDates([walking.from.clone().tz(tz).startOf('day')]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useNonInitialEffect(() => {
        const serviceChanged = walking?.service?.serviceId !== walkingState?.service?.serviceId;
        const fromChanged =
            (walking?.from && !walkingState?.from) ||
            (!walking?.from && walkingState?.from) ||
            (walking?.from && walkingState?.from && !walking.from.isSame(walkingState.from, 'minute'));
        const toChanged =
            (walking?.to && !walkingState?.to) ||
            (!walking?.to && walkingState?.to) ||
            (walking?.to && walkingState?.to && !walking.to.isSame(walkingState.to, 'minute'));

        if (serviceChanged || fromChanged || toChanged) {
            setWalking(normalizeWalkingDates(walking));
            if (walking?.selectedDates?.length) {
                setSelectedDates(walking.selectedDates.map(d => d.clone().tz(tz).startOf('day')));
            } else if (walking?.from?.isValid()) {
                setSelectedDates([walking.from.clone().tz(tz).startOf('day')]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [walking?.service?.serviceId, walking?.from, walking?.to]);

    // On edit the catalog can arrive after the form mounts, and it decides whether the service
    // runs on sessions, so its booking type and location replace what the appointment carried.
    useNonInitialEffect(() => {
        const catalogService = walking?.service;
        if (!catalogService || catalogService.serviceId !== walkingState?.service?.serviceId) return;

        setWalking(prev => ({
            ...prev!,
            service: {
                ...prev!.service!,
                bookingType: catalogService.bookingType,
                maxPets: catalogService.maxPets,
                inHomeService: catalogService.inHomeService
            }
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [walking?.service?.bookingType, walking?.service?.maxPets, walking?.service?.inHomeService]);

    useNonInitialEffect(() => {
        setLoading(clientsStatus === ClientsStatus.Getting);
    }, [clientsStatus]);

    useClientAddressPrefill(clientId, Boolean(walkingState?.service?.inHomeService), walkingState?.address, line =>
        setWalking(prev => ({ ...prev!, address: line }))
    );

    const {
        sessionBased,
        sessionDate,
        sessions,
        loading: sessionsLoading,
        selectedSessionId,
        sessionStart,
        multiDate,
        sessionDays,
        toggleSession,
        occurrences
    } = useSessionPicker({
        area: SERVICES_BUSINESS_AREAS.WALKING,
        serviceId: walkingState?.service?.serviceId,
        bookingType: walkingState?.service?.bookingType,
        inHomeService: walkingState?.service?.inHomeService,
        isEdit,
        from: walkingState?.from,
        staffId: walkingState?.staffId,
        serviceSessionId: walkingState?.serviceSessionId,
        selectedDates,
        drafts: draftSessionBookings,
        appointmentId
    });

    // Several dates: each selected session is checked on its own and its alerts go under its card.
    const sessionAlertSlots = useMemo(
        () =>
            occurrences.map(occurrence => ({
                key: `${occurrence.day}|${occurrence.serviceSessionId}`,
                from: occurrence.from,
                staffId: occurrence.staffId
            })),
        [occurrences]
    );
    const sessionAlerts = useSessionAlerts({
        clientId,
        petId,
        serviceId: walkingState?.service?.serviceId,
        appointmentId,
        addressOverride,
        slots: sessionAlertSlots,
        enabled: multiDate && sessionBased
    });

    const selectSessionHandler = (session: WalkingSession) => {
        const from = sessionStart(session);

        setWalking(prev => ({
            ...prev!,
            staffId: session.staffId,
            serviceSessionId: session.id,
            from,
            to: from.clone().add(prev?.service?.duration || 60, 'minutes')
        }));
    };

    const changePeriodHandler = (from: Moment) => {
        // A walk ends when its service duration is up. The saved dateEnd is derived from
        // `to` (`walkingDateSlots.ts`), so an editable end time would outrank the catalog.
        const newTo = from.clone().add(walkingState?.service?.duration || 60, 'minutes');
        const datesForEdit = [from.clone().tz(tz).startOf('day')];
        if (isEdit) {
            setSelectedDates(datesForEdit);
        }
        setWalking(prev => {
            // The date pickers echo their props back, so only a real move off the session
            // start clears the pick.
            const startMoved = !prev?.from?.isValid() || !from.isSame(prev.from, 'minute');

            return {
                ...prev!,
                ...(startMoved ? { serviceSessionId: null } : {}),
                from: from.clone().tz(timeZone),
                to: newTo.clone().tz(timeZone)
            };
        });
    };

    const changeSelectedDatesHandler = (datesOrUpdater: SetStateAction<Array<Moment>>) => {
        const next = typeof datesOrUpdater === 'function' ? datesOrUpdater(selectedDates) : datesOrUpdater;
        const normalized = next.map(d => d.clone().tz(tz).startOf('day'));
        const datesChanged =
            normalized.length !== selectedDates.length ||
            normalized.some((date, index) => !date.isSame(selectedDates[index], 'day'));

        setSelectedDates(normalized);
        if (datesChanged) setWalking(prev => ({ ...prev!, serviceSessionId: null }));
    };

    const effectiveDurationMinutes =
        walkingState?.service?.duration && walkingState.service.duration > 0
            ? walkingState.service.duration
            : walkingState?.from?.isValid() && walkingState?.to?.isValid()
            ? Math.max(0, walkingState.to.diff(walkingState.from, 'minutes'))
            : walkingState?.service?.duration ?? 0;

    const effectiveServicePrice = walkingState?.service?.price ?? walkingState?.price;

    const allowZeroPrice = enrollment?.id != null;
    const priceInvalid =
        effectiveServicePrice == null ||
        Number.isNaN(Number(effectiveServicePrice)) ||
        (allowZeroPrice ? Number(effectiveServicePrice) < 0 : Number(effectiveServicePrice) <= 0);

    // Picked sessions carry their own staff, so the staff field only matters for a date without one.
    const everyDateHasSession =
        multiDate && sessionDays.length > 0 && sessionDays.every(entry => entry.selectedSessionIds.length > 0);

    // The typed time is only booked on a date without a picked session, so it is only checked then.
    const { routedAlerts: typedTimeAlerts, dateTimeAlerts: typedTimeDateTimeAlerts } = useBookingAlerts({
        clientId,
        petId,
        from: walkingState?.from,
        to: walkingState?.to,
        serviceId: walkingState?.service?.serviceId,
        staffId: walkingState?.staffId,
        appointmentId,
        addressOverride,
        enabled: !everyDateHasSession
    });
    // Alerts every selected session returned, and the pet and client ones, show once on the form.
    const { shared: sharedSessionAlerts, bySlot: cardAlerts } = useMemo(
        () => splitSessionAlerts(sessionAlerts),
        [sessionAlerts]
    );
    const routedAlerts = useMemo(
        () => mergeRoutedAlerts(typedTimeAlerts, sharedSessionAlerts),
        [typedTimeAlerts, sharedSessionAlerts]
    );
    const dateTimeAlerts = useMemo(
        () =>
            mergeRoutedAlerts({ ...typedTimeAlerts, dateTime: typedTimeDateTimeAlerts }, sharedSessionAlerts).dateTime,
        [typedTimeAlerts, typedTimeDateTimeAlerts, sharedSessionAlerts]
    );

    const isDisabled = isSaveDisabled({
        hasService: Boolean(walkingState?.service),
        hasStaff: Boolean(walkingState?.staffId) || everyDateHasSession,
        needsAddress: Boolean(walkingState?.service?.inHomeService),
        hasAddress: Boolean(walkingState?.address?.trim()),
        durationMinutes: effectiveDurationMinutes,
        priceInvalid,
        missingDates: !isEdit && selectedDates.length === 0
    });

    const periodFrom = walkingState?.from?.isValid()
        ? walkingState.from.clone().tz(timeZone)
        : moment.tz(timeZone).seconds(0).milliseconds(0);
    const periodTo = walkingState?.to?.isValid()
        ? walkingState.to.clone().tz(timeZone)
        : periodFrom.clone().add(walkingState?.service?.duration || 60, 'minutes');

    return (
        <div className={styles.walkingContainer}>
            <div className={styles.configContainer}>
                <div className={styles.section}>
                    {isEdit ? (
                        <PeriodSelector
                            from={periodFrom}
                            to={periodTo}
                            labelDate="Date"
                            labelTimeStart="Start Time"
                            hideEndTime
                            isRecurrent={false}
                            frequencyOptions={[]}
                            alerts={dateTimeAlerts}
                            onChangeFrequencyDuration={noop}
                            onChangePeriod={changePeriodHandler}
                        />
                    ) : (
                        <MultipleDateSelector
                            from={periodFrom}
                            to={periodTo}
                            selectedDates={selectedDates}
                            labelDate="Date"
                            labelTimeStart="Start Time"
                            hideEndTime
                            isRecurrent={false}
                            frequencyOptions={[]}
                            alerts={dateTimeAlerts}
                            onChangeFrequencyDuration={noop}
                            onChangeSelectedDates={changeSelectedDatesHandler}
                            onChangePeriod={changePeriodHandler}
                        />
                    )}
                </div>
                {routedAlerts.general.length > 0 && (
                    <div className={styles.section}>
                        <BookingAlertList alerts={routedAlerts.general} />
                    </div>
                )}
                <div className={styles.section}>
                    <RecentlyBooked
                        clientId={clientId}
                        petId={petId}
                        refreshTrigger={recentlyBookedRefreshKey}
                        onViewAll={() => setShowAppointmentHistoryDrawer(true)}
                        onAppointmentClick={(appointment: AppointmentListItem) => {
                            if (appointment.orderId != null && appointment.bookingId != null) {
                                setShowOrderDetail({ orderId: appointment.orderId, bookingId: appointment.bookingId });
                            }
                        }}
                    />
                </div>
                <div className={styles.section}>
                    <WalkingServiceSelector
                        loading={servicesStatus === ServicesStatus.FetchingWalkingServices}
                        className={styles.serviceSelector}
                        value={walkingState?.service}
                        inHomeService={inHomeServiceState}
                        onChange={value => {
                            const previousServiceId = walkingState?.service?.serviceId;
                            const newServiceId = value?.serviceId;
                            const catalogPrice = value?.price || 0;
                            const sameService = previousServiceId === newServiceId;
                            const keepEditedPrice = sameService && walkingState?.price != null;
                            const resolved = resolveStandardPrice(
                                catalogPrice,
                                customPrices || [],
                                value?.serviceId || 0
                            );

                            setWalking({
                                ...walkingState!,
                                service: value!,
                                ...(sameService ? {} : { serviceSessionId: null }),
                                to:
                                    walkingState?.from?.isValid() && value?.duration
                                        ? walkingState.from.clone().add(value.duration, 'minutes')
                                        : walkingState?.to,
                                price: keepEditedPrice ? walkingState!.price : resolved.price,
                                customPriceId: sameService
                                    ? walkingState?.customPriceId ?? resolved.customPriceId
                                    : resolved.customPriceId,
                                useSavedCustomPrice: sameService ? walkingState?.useSavedCustomPrice : undefined,
                                saveCustomPrice: sameService ? walkingState?.saveCustomPrice : false
                            });

                            if (previousServiceId !== newServiceId) {
                                setInHomeService(undefined);
                            }
                        }}
                        petId={petId}
                        servicesUsed={servicesUsed}
                    />
                </div>

                {walkingState?.service && sessionBased && (
                    <div className={styles.section}>
                        <SessionSelector
                            sessions={sessions}
                            loading={sessionsLoading}
                            date={sessionDate}
                            selectedSessionId={selectedSessionId}
                            onSelect={selectSessionHandler}
                            groups={
                                multiDate
                                    ? sessionDays.map(entry => ({
                                          key: entry.day,
                                          date: entry.date,
                                          sessions: entry.sessions,
                                          loading: entry.loading,
                                          selectedSessionIds: entry.selectedSessionIds,
                                          alertsBySessionId: Object.fromEntries(
                                              entry.selectedSessionIds
                                                  .filter(id => cardAlerts[`${entry.day}|${id}`])
                                                  .map(id => [id, cardAlerts[`${entry.day}|${id}`]])
                                          )
                                      }))
                                    : undefined
                            }
                            onSelectInGroup={toggleSession}
                            serviceName={walkingState.service.serviceName}
                        />
                        <BookingAlertList alerts={routedAlerts.session} />
                        {!multiDate && routedAlerts.sessionFull.length > 0 && (
                            <SessionFullAlert serviceName={walkingState.service.serviceName} />
                        )}
                    </div>
                )}

                {walkingState?.service && walkingState?.service?.inHomeService && (
                    <div className={styles.section}>
                        <p className={styles.title}>Client Address</p>
                        {loading ? (
                            <Spinner />
                        ) : (
                            <ClientAddressAutocomplete
                                value={address}
                                className={styles.input}
                                onChange={value =>
                                    setWalking({
                                        ...walkingState!,
                                        address: value
                                    })
                                }
                                onSelectPlace={(place: SelectedPlace) =>
                                    setWalking(prev => ({
                                        ...prev!,
                                        address: place.address,
                                        addressOverride: {
                                            latitude: place.latitude,
                                            longitude: place.longitude,
                                            zipcode: place.zipcode
                                        }
                                    }))
                                }
                            />
                        )}
                        <BookingAlertList alerts={routedAlerts.zone} />
                    </div>
                )}

                {walkingState?.service && walkingState?.service?.inHomeService && (
                    <div className={styles.section}>
                        <p className={styles.title}>Pick-up Instructions</p>
                        <Input
                            value={walkingState?.instructions ?? ''}
                            className={styles.input}
                            placeholder="Gate code, where to meet, where the keys are"
                            onChange={event =>
                                setWalking({
                                    ...walkingState!,
                                    instructions: event.target.value
                                })
                            }
                        />
                    </div>
                )}

                {walkingState?.service && (
                    <div className={styles.section}>
                        <div className={styles.serviceData}>
                            <DurationSelect
                                label="Duration"
                                selectedOption={walkingState?.service}
                                onSelect={value => {
                                    const nextService = value as WalkingService;
                                    const nextDuration = nextService?.duration ?? 0;
                                    const nextTo =
                                        walkingState?.from?.isValid() && nextDuration > 0
                                            ? walkingState.from.clone().add(nextDuration, 'minutes')
                                            : walkingState?.to;
                                    setWalking({
                                        ...walkingState!,
                                        service: nextService,
                                        to: nextTo
                                    });
                                }}
                                type="walking"
                            />
                            <div
                                className={clsx(styles.PriceSection, {
                                    [lockStyles.interactionLock]: !canEditPrices
                                })}
                            >
                                <PriceField
                                    id="booking_service_input_price"
                                    label="Price"
                                    value={walkingState?.price || walkingState?.service?.price}
                                    onChange={value =>
                                        setWalking({
                                            ...walkingState!,
                                            price: value!
                                        })
                                    }
                                    className={styles.price}
                                    placeholder="$0.00"
                                    scale={2}
                                    precision={5}
                                />
                            </div>
                        </div>
                        {savedCustomPrice.show && (
                            <SavedCustomPriceChoice
                                petName={savedCustomPrice.petName}
                                savedPrice={savedCustomPrice.savedPrice}
                                mode={savedCustomPrice.mode}
                                disabled={!canEditPrices}
                                onChange={mode => {
                                    const next = savedCustomPrice.applyMode(mode);
                                    setWalking({
                                        ...walkingState!,
                                        price: next.price,
                                        useSavedCustomPrice: next.useSavedCustomPrice,
                                        customPriceId: next.customPriceId
                                    });
                                }}
                            />
                        )}
                        <SaveCustomPriceCheckbox
                            checked={!!walkingState?.saveCustomPrice}
                            disabled={!canEditPrices}
                            currentPrice={walkingState?.price}
                            defaultPrice={Number(walkingState?.service?.price) || 0}
                            onChange={checked =>
                                setWalking({
                                    ...walkingState!,
                                    saveCustomPrice: checked
                                })
                            }
                        />
                    </div>
                )}

                {walkingState?.service && (
                    <div
                        className={clsx(styles.section, {
                            [lockStyles.interactionLock]: !canApplyDiscount && hasDiscount,
                            [lockStyles.hiddenComponent]: !canApplyDiscount && !hasDiscount
                        })}
                    >
                        <ValueTypeSelector
                            label="Discount"
                            value={walkingState?.discount}
                            className={styles.discount}
                            max={
                                walkingState?.discount?.type === 'percentage'
                                    ? 100
                                    : walkingState?.discount?.type === 'amount'
                                    ? walkingState?.service?.price
                                    : 0.5
                            }
                            onChange={value =>
                                setWalking({
                                    ...walkingState!,
                                    discount: value!
                                })
                            }
                        />
                    </div>
                )}

                {walkingState?.service && walkingState?.service?.serviceId && (
                    <div className={styles.section}>
                        <StaffSelector
                            loading={
                                !walkingState?.service || staffStatus === StaffStatus.Fetching || staffs.length === 0
                            }
                            type="walking"
                            key={walkingState?.service?.serviceId}
                            selectedStaff={walkingState?.staffId}
                            autoSelect={false}
                            service={walkingState?.service}
                            alerts={routedAlerts.staff}
                            onSelectStaff={value =>
                                setWalking(prev => ({
                                    ...prev!,
                                    staffId: value?.id,
                                    ...(prev?.staffId !== value?.id ? { serviceSessionId: null } : {})
                                }))
                            }
                        />
                    </div>
                )}
            </div>
            <div className={styles.footerContainer}>
                <Button
                    isLoading={
                        newBookingStatus === NewBookingsStatus.SavingCreateAppointment ||
                        newBookingStatus === NewBookingsStatus.SingleRescheduling ||
                        ordersStatus === OrdersStatus.Reloading
                    }
                    className={styles.buttonSave}
                    disabled={isDisabled}
                    variant="green"
                    label="Save"
                    size="large"
                    onClick={() => {
                        const selectedSession = sessions.find(session => session.id === selectedSessionId);
                        // Send the session the form shows as selected, never a stale stored link.
                        // Its start pins `from` to the session's day so the slot keeps the link.
                        const sessionFrom = selectedSession ? sessionStart(selectedSession) : undefined;
                        const normalizedWalking = normalizeWalkingDates({
                            ...walkingState,
                            ...(sessionFrom
                                ? {
                                      from: sessionFrom,
                                      to: walkingState.to?.isValid()
                                          ? sessionFrom
                                                .clone()
                                                .add(walkingState.to.diff(walkingState.from, 'minutes'), 'minutes')
                                          : walkingState.to
                                  }
                                : {}),
                            serviceSessionId: selectedSession?.id ?? null
                        });
                        const resolvedSelectedDates = isEdit
                            ? [normalizedWalking.from.clone().tz(tz).startOf('day')]
                            : selectedDates.map(d => d.clone().tz(tz).startOf('day'));
                        const payload: Walking = {
                            ...normalizedWalking,
                            selectedDates: resolvedSelectedDates,
                            sessionSlots: multiDate && sessionBased ? occurrences : undefined
                        };
                        if (enrollment != null) {
                            payload.enrollment = enrollment;
                        } else {
                            delete (payload as { enrollment?: unknown }).enrollment;
                        }
                        if (membershipItem != null) {
                            payload.membershipItem = membershipItem;
                        } else {
                            delete (payload as { membershipItem?: unknown }).membershipItem;
                        }
                        onNext(payload);
                    }}
                />
            </div>
            <AppointmentHistoryDrawer
                open={showAppointmentHistoryDrawer}
                onBack={() => {
                    setShowAppointmentHistoryDrawer(false);
                    setRecentlyBookedRefreshKey(k => k + 1);
                }}
                customerId={clientId}
                petId={petId}
                onAppointmentClick={(appointment: AppointmentListItem) => {
                    if (appointment.orderId != null && appointment.bookingId != null) {
                        setShowOrderDetail({ orderId: appointment.orderId, bookingId: appointment.bookingId });
                    }
                }}
            />
            {showOrderDetail && (
                <OrderDetail
                    orderId={showOrderDetail.orderId}
                    bookingId={showOrderDetail.bookingId}
                    readOnly={true}
                    onClose={() => setShowOrderDetail(null)}
                    zIndex={10100}
                />
            )}
        </div>
    );
};

export default WalkingComponent;
